import { Box } from "@mui/material";
import { tableScrollClassName, wrapTablesInScrollContainers } from "./PolicyPdfRichText.utils";

export interface PolicyPdfRichTextProps {
    html: string;
    // Lets a wide imported table be panned sideways on a phone. Off for the PDF: a non-visible overflow
    // stops the headless browser fragmenting a tall table across pages.
    scrollableTables?: boolean;
}

// The policy editors store TipTap HTML. The interactive editor components cannot be reused for PDF
// rendering: the ProseMirror scroll container is monolithic for CSS fragmentation, so the headless
// browser cannot break it across pages — content gets pushed, sliced mid-line and painted over the
// page margin. Rendering the stored HTML statically lets it flow naturally across page breaks.
function PolicyPdfRichText({ html, scrollableTables }: PolicyPdfRichTextProps) {
    return (
        <Box
            sx={(theme) => ({
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.pxToRem(14),
                lineHeight: 1.5,
                // The editor serializes blank lines as empty paragraphs — keep them one line tall
                "& p": { margin: 0, minHeight: "1.5em" },
                // Keep headings inside the content on the same page as the text that follows them
                "& h1, & h2, & h3, & h4": { breakAfter: "avoid" },
                "& ul, & ol": { margin: 0, paddingLeft: "1.5em" },
                "& a": { color: theme.palette.primary.main, textDecoration: "underline" },
                "& img": { maxWidth: "100%", borderRadius: "4px", margin: "8px 2px", breakInside: "avoid" },
                "& table": { borderCollapse: "collapse", width: "100%", margin: "12px 0", tableLayout: "fixed" },
                "& th, & td": {
                    border: `1px solid ${theme.palette.divider}`,
                    padding: "4px 8px",
                    verticalAlign: "top",
                    textAlign: "left",
                },
                "& th": { backgroundColor: theme.palette.action.hover, fontWeight: 600 },
                // Narrow tables still fill the width, wide ones pan inside their own wrapper rather than
                // dragging the whole page sideways.
                ...(scrollableTables
                    ? {
                          [theme.breakpoints.down("sm")]: {
                              [`& .${tableScrollClassName}`]: { overflowX: "auto", margin: "12px 0" },
                              // max-content lets a cell hold its longest line, which one long sentence turns into
                              // four screens of panning. Capping the table at twice the screen makes those cells
                              // wrap instead, while a genuinely many-columned table still scrolls.
                              "& table": { width: "max-content", minWidth: "100%", maxWidth: "200%", margin: 0, tableLayout: "auto" },
                              "& th, & td": { overflowWrap: "break-word" },
                          },
                      }
                    : {}),
            })}
            dangerouslySetInnerHTML={{ __html: scrollableTables ? wrapTablesInScrollContainers(html) : html }}
        />
    );
}

export default PolicyPdfRichText;
